import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent } from '@/components/ui-kit/tabs';
import {
  ContractsTabsList,
  type ContractsTabsListOption,
} from '@/modules/org/contracts/components/contracts-tabs-list';
import {
  downloadTemplateJson,
  exportTemplate,
} from '@/modules/admin/templates/lib/export-import.service';
import { useGetTemplateBySlug } from '@/modules/templates/hooks/use-templates';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { useGetTemplatePageBySlug } from '@/modules/admin/hooks/use-template-pages';
import { useLanguageContext } from '@/i18n/language-context';
import { recordToTemplatePage } from '../types/landing-page.types';
import { ArrowLeft, Download, Loader2, MoreVertical, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { getIconComponent } from '../constants/icon-options';
import { DetailsTab } from '../components/edit-page-tabs/details-tab';
import { FormBuilderTab } from '../components/edit-page-tabs/form-builder-tab';
import { LandingPageTab } from '../components/edit-page-tabs/landing-page-tab';
import { TranslationsTab } from '../components/edit-page-tabs/translation-tab';
import { useTemplateEditUiStore } from '../store/use-template-edit-ui-store';

export default function EditTemplatePage() {
  const params = useParams();
  const templateId = params.id as string;
  const { t } = useTranslation();

  // Use UDS hook to fetch template with screens & fields
  const {
    data: templateData,
    isLoading: templateLoading,
    refetch: refetchTemplate,
  } = useGetTemplateBySlug(templateId);

  const [template, setTemplate] = useState<TemplateWithScreens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentLanguage } = useLanguageContext();

  // Live icon value lifted from DetailsTab so the page header reflects the
  // user's current icon-picker selection before they save.
  const [headerIconValue, setHeaderIconValue] = useState<string>('FileText');

  // Header export action — the dialog itself stays mounted in DetailsTab.
  const [isExporting, setIsExporting] = useState(false);
  const openOverwriteDialog = useTemplateEditUiStore((s) => s.openOverwriteDialog);

  const handleExportTemplate = async () => {
    if (!template) return;
    setIsExporting(true);
    try {
      const data = await exportTemplate(template.slug);
      if (data) downloadTemplateJson(data);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Tabs definition — feeds the reusable ContractsTabsList. First 3 stay
  // inline on mobile, "Translations" drops into the "More" overflow.
  type EditTabValue = 'details' | 'form-builder' | 'landing-page' | 'translations';
  const [activeTab, setActiveTab] = useState<EditTabValue>('details');
  const editTabs: ReadonlyArray<ContractsTabsListOption<EditTabValue>> = [
    { value: 'details', labelKey: 'ORGTEMPLATES_EDIT_TABS_DETAILS' },
    { value: 'form-builder', labelKey: 'ORGTEMPLATES_EDIT_TABS_FORMBUILDER' },
    { value: 'landing-page', labelKey: 'ADMIN_TEMPLATES_EDIT_TABS_LANDINGPAGE' },
    { value: 'translations', labelKey: 'ADMIN_TEMPLATES_EDIT_TABS_TRANSLATIONS' },
  ];

  // Populate state when templateData loads from the hook
  useEffect(() => {
    if (templateData) {
      setTemplate(templateData);
      setHeaderIconValue(templateData.icon ?? 'FileText');
      setIsLoading(false);
    }
  }, [templateData]);

  // Mark loading done when hook finishes
  useEffect(() => {
    if (!templateLoading && !templateData) {
      setIsLoading(false);
    }
  }, [templateLoading, templateData]);

  // Landing page (TemplatePages UDS) — fetched once we know the slug + the
  // active locale. The LandingPageTab owns the form/CRUD; the page just
  // forwards the loaded record so the tab badge can reflect existence.
  const { data: landingPageRecord } = useGetTemplatePageBySlug(
    template?.slug,
    currentLanguage
  );
  const landingPage = useMemo(
    () => (landingPageRecord ? recordToTemplatePage(landingPageRecord) : null),
    [landingPageRecord]
  );
  const landingPageExists = !!landingPageRecord;

  const HeaderIconComponent = getIconComponent(headerIconValue);

  if (isLoading) {
    return (
      <div className="w-full mx-auto max-w-[95%] xl:max-w-400 py-8 px-4">
        <div className="flex items-center gap-4 mb-8">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="space-y-6">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">
              {t('ADMIN_TEMPLATES_EDIT_NOTFOUND_TITLE')}
            </CardTitle>
            <CardDescription>{t('ADMIN_TEMPLATES_EDIT_NOTFOUND_DESCRIPTION')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to={`/admin/templates`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('ADMIN_TEMPLATES_EDIT_NOTFOUND_BACKACTION')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full py-3 md:py-8 px-2 md:px-4">
      {/* Header — title + status badge inline on the left, Export /
          Overwrite actions on the right. Stacks below sm for narrow phones. */}
      <div className="flex  gap-3 items-center justify-between mb-5 md:mb-8">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <Link to={`/admin/templates`} className="shrink-0">
            <button>
              <ArrowLeft className="h-5 w-5" />
            </button>
          </Link>
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <div className="h-10 w-10 hidden md:flex md:h-12 md:w-12 shrink-0 rounded-xl bg-selise-blue/10  items-center justify-center">
              <HeaderIconComponent className="h-5 w-5 md:h-6 md:w-6 text-selise-blue" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--fg))] truncate">
                  {t('ORGTEMPLATES_EDIT_HEADER_TITLE')}
                </h1>
                <Badge
                  variant={template.available ? 'default' : 'secondary'}
                  className={
                    template.available
                      ? 'bg-lime-green/10 text-poly-green shrink-0'
                      : 'bg-[hsl(var(--globe-grey))]/10 text-globe-grey shrink-0'
                  }
                >
                  {template.available
                    ? t('ADMIN_TEMPLATES_TABLE_BADGES_AVAILABLE')
                    : t('ADMIN_TEMPLATES_TABLE_BADGES_COMINGSOON')}
                </Badge>
              </div>
              <p className="text-xs md:text-sm text-globe-grey truncate">{template.title}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 self-end md:self-auto shrink-0">
          {/* Desktop: inline buttons. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="hidden md:inline-flex gap-2"
            onClick={handleExportTemplate}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export JSON
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="hidden md:inline-flex gap-2"
            onClick={openOverwriteDialog}
          >
            <Upload className="h-4 w-4" />
            Overwrite from JSON
          </Button>

          {/* Mobile: collapse both actions into a 3-dot dropdown. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="md:hidden h-9 w-9"
                aria-label="Template actions"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onSelect={handleExportTemplate} disabled={isExporting}>
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export JSON
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={openOverwriteDialog}
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Upload className="h-4 w-4" />
                Overwrite from JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as EditTabValue)}
        className="space-y-6"
      >
        <ContractsTabsList<EditTabValue>
          options={editTabs}
          activeValue={activeTab}
          onSelect={setActiveTab}
          visibleCount={2}
        />

        <TabsContent value="details">
          {templateData ? (
            <DetailsTab
              template={template}
              templateData={templateData}
              refetchTemplate={refetchTemplate}
              onIconChange={setHeaderIconValue}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="form-builder">
          <FormBuilderTab templateId={templateId} />
        </TabsContent>

        <TabsContent value="landing-page">
          <LandingPageTab
            template={template}
            landingPage={landingPage}
            landingPageExists={landingPageExists}
            isLoading={isLoading}
          />
        </TabsContent>
        {/* Translations Tab */}
        <TabsContent value="translations">
          <TranslationsTab
            templateId={template.id}
            templateSlug={template.slug}
            templateTitle={template.title}
            templateDescription={template.description ?? ''}
            uilmTitleKey={template.uilmTitleKey}
            uilmDescriptionKey={template.uilmDescriptionKey}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
